// Status Badge Component for File Version Control
import { FileStatus } from '@types';
import { cn } from '@utils/classNames';

interface StatusBadgeProps {
  status?: FileStatus;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const STATUS_CONFIG = {
  [FileStatus.WIP]: {
    label: 'WIP',
    icon: '🚧',
    bgColor: 'bg-orange-500/20',
    textColor: 'text-orange-400',
    borderColor: 'border-orange-500/30',
  },
  [FileStatus.DRAFT]: {
    label: 'Draft',
    icon: '🟡',
    bgColor: 'bg-yellow-500/20',
    textColor: 'text-yellow-400',
    borderColor: 'border-yellow-500/30',
  },
  [FileStatus.TAGGED]: {
    label: 'Tagged',
    icon: '🏷️',
    bgColor: 'bg-blue-500/20',
    textColor: 'text-blue-400',
    borderColor: 'border-blue-500/30',
  },
  [FileStatus.UNTAGGED]: {
    label: 'Clean',
    icon: '✨',
    bgColor: 'bg-purple-500/20',
    textColor: 'text-purple-400',
    borderColor: 'border-purple-500/30',
  },
  [FileStatus.READY]: {
    label: 'Ready',
    icon: '🟢',
    bgColor: 'bg-green-500/20',
    textColor: 'text-green-400',
    borderColor: 'border-green-500/30',
  },
  [FileStatus.SOLD]: {
    label: 'Sold',
    icon: '✅',
    bgColor: 'bg-primary/20',
    textColor: 'text-primary',
    borderColor: 'border-primary/30',
  },
  [FileStatus.ARCHIVED]: {
    label: 'Archived',
    icon: '⚫',
    bgColor: 'bg-gray-500/20',
    textColor: 'text-gray-400',
    borderColor: 'border-gray-500/30',
  },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  status, 
  size = 'md',
  className 
}) => {
  if (!status) return null;

  const config = STATUS_CONFIG[status];
  
  // Fallback for unknown status
  if (!config) {
    console.warn('Unknown status:', status);
    return null;
  }
  
  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-[10px]',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm',
  };

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-full border font-inter font-semibold whitespace-nowrap',
        size === 'xs' ? 'gap-1' : 'gap-1.5',
        config.bgColor,
        config.textColor,
        config.borderColor,
        sizeClasses[size],
        className
      )}
    >
      <span className={size === 'xs' ? 'text-[9px]' : ''}>{config.icon}</span>
      <span>{config.label}</span>
    </div>
  );
};

